import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { ownerPermissions } from 'constants/auth';
import { useDispatch, useSelector } from 'react-redux';
import { SettingsTextFieldConfirm } from 'components/SettingsNew';

import { fetchMatrix } from 'actions/matrices';
import './MatrixSettings.scss';
import { addEmailToMatrix, removeEmailFromMatrix } from 'services/users';
import ButtonNew from 'components/inputs/ButtonNew';
import MiniLoader from 'components/MiniLoader';

const MatrixSharingSettings = ({ matrix, isStatic }) => {
  const dispatch = useDispatch();

  const [tempEmail, setTempEmail] = useState('');
  const [result, setResult] = useState(null);

  const [addLoading, setAddLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  const auth = useSelector(state => state.auth);
  const userId = useMemo(() => auth.user?.claims?.user_id ?? null, [auth.user]);

  const isMatrixOwner = useMemo(() => {
    return ownerPermissions.includes(matrix?.roles?.[userId]);
  }, [matrix, userId]);

  const setAddError = v => {
    setResult(v);
    setTimeout(() => setResult(null), 2500);
  };

  const addEditor = useCallback(() => {
    if (matrix) {
      setAddLoading(true);
      addEmailToMatrix(matrix.id, tempEmail, userId).then(v => {
        const { data } = v;
        const { success } = data;
        if (success) {
          dispatch(fetchMatrix(matrix.id));
          setTempEmail('');
        } else {
          setAddError(data.errorMessage);
        }
        setAddLoading(false);
      });
    }
  }, [dispatch, matrix, userId, tempEmail]);

  const removeEditor = useCallback(
    idToRemove => {
      if (matrix) {
        setRemoveLoading(true);
        removeEmailFromMatrix(matrix.id, userId, idToRemove).then(v => {
          const { data } = v;
          const { success } = data;
          if (success) {
            dispatch(fetchMatrix(matrix.id));
          }
          setRemoveLoading(false);
        });
      }
    },
    [dispatch, matrix, userId]
  );

  // TODO Make sure email address looks correct and set a validation message
  const isEmailInvalid = str => {
    if (!str) return null;
    const error = 'Email address not valid';
    const parts = str.split('@');
    if (parts.length !== 2) return error;
    const [address, host] = parts;
    const invalidAddress = host.split('.').length !== 2;
    if (invalidAddress) return error;
    return null;
  };

  const writeAccessUsers = useMemo(() => {
    const { roles, additionalRoleNames } = matrix;

    const writeUserIds = Object.entries(roles)
      .filter(([k, v]) => v === 'write')
      .map(([k, v]) => k);

    const writeUserEmails = writeUserIds.map(v => ({
      id: v,
      email: additionalRoleNames[v],
    }));

    return writeUserEmails;
  }, [matrix]);

  if (!isMatrixOwner) return null;

  return (
    <div className="MatrixSettings-row">
      <div className="MatrixSettings-column MatrixSettings-half">
        <SettingsTextFieldConfirm
          label={'Share edit access'}
          onChange={value => setTempEmail(value)}
          placeholder="Enter an email"
          disabled={!tempEmail || isEmailInvalid(tempEmail)}
          isLoading={addLoading}
          onConfirm={addEditor}
          validationMessage={isEmailInvalid(tempEmail) || result}
          value={tempEmail}
          description={
            isStatic
              ? 'Emails of users with edit access'
              : 'Add the email of another user to share edit access for your matrix.'
          }
          isStatic={isStatic}
        />
      </div>
      <div className="MatrixSettings-column MatrixSettings-half">
        <div className="MatrixSettings-token-container">
          {writeAccessUsers.map((user, i) => (
            <div className="MatrixSettings-token" key={i}>
              {user.email}
              <div className="MatrixSettings-token-button">
                {removeLoading ? (
                  <MiniLoader />
                ) : (
                  <ButtonNew
                    icon="trash"
                    buttonClasses="icon-button-small"
                    onClick={() => removeEditor(user.id)}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MatrixSharingSettings;
